import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { TestRunnerService } from './services/test-runner.service';
import { TestEngineService } from './services/test-engine.service';
import { TestReport, TestSuite, TestStatus } from './models/test-result.model';

interface RouteInfo {
    path: string;
    name: string;
    component: string;
    description: string;
    isRunning: boolean;
    progress: number;
    status: string;
    lastResult?: TestSuite;
}

@Component({
    selector: 'app-ui-test-dashboard',
    standalone: true,
    imports: [CommonModule, FormsModule],
    template: `
        <div class="test-dashboard">
            <div class="header">
                <h1>🧪 UI Test Dashboard</h1>
                <p>Automated testing for all routes in Otto Quote Assist</p>
                
                <div class="action-buttons">
                    <button 
                        class="btn btn-primary btn-lg"
                        (click)="runAllTests()"
                        [disabled]="isRunningAllTests">
                        {{ isRunningAllTests ? 'Running All Tests...' : '🚀 Run All Tests' }}
                    </button>
                    
                    <button 
                        class="btn btn-success"
                        (click)="downloadLastReport()"
                        [disabled]="!lastTestReport">
                        📥 Download Report
                    </button>
                    
                    <button 
                        class="btn btn-info"
                        (click)="viewLastReport()"
                        [disabled]="!lastTestReport">
                        👁️ View Report
                    </button>
                </div>
            </div>

            <!-- Overall Progress -->
            <div class="overall-progress" *ngIf="isRunningAllTests">
                <h3>Overall Progress</h3>
                <div class="progress-bar">
                    <div class="progress-fill" [style.width.%]="overallProgress"></div>
                </div>
                <p>{{ overallStatus }}</p>
            </div>

            <!-- Route Test Cards -->
            <div class="routes-grid">
                <div class="route-card" *ngFor="let route of routes" 
                     [ngClass]="{ 
                         'running': route.isRunning, 
                         'passed': route.lastResult?.status === 'passed',
                         'failed': route.lastResult?.status === 'failed' 
                     }">
                    
                    <div class="route-header">
                        <h3>{{ route.name }}</h3>
                        <span class="route-path">{{ route.path || '/' }}</span>
                    </div>
                    
                    <p class="route-description">{{ route.description }}</p>
                    
                    <div class="route-info">
                        <small><strong>Component:</strong> {{ route.component }}</small>
                    </div>

                    <!-- Direct Quote Assist Configuration (only for Direct Quote Assist) -->
                    <div class="quote-assist-config" *ngIf="route.name === 'Direct Quote Assist'">
                        <h4>⚙️ RFQ Configuration</h4>
                        <p><small>Configure RFQ parameters for this test:</small></p>
                        
                        <div class="config-form compact">
                            <div class="form-row">
                                <div class="form-group">
                                    <label for="rfqId">RFQ ID:</label>
                                    <input 
                                        id="rfqId"
                                        type="text" 
                                        [(ngModel)]="rfqParams.rfqId" 
                                        class="form-control"
                                        placeholder="Enter RFQ ID">
                                </div>
                                <div class="form-group">
                                    <label for="companyId">Company ID:</label>
                                    <input 
                                        id="companyId"
                                        type="text" 
                                        [(ngModel)]="rfqParams.companyId" 
                                        class="form-control"
                                        placeholder="Enter Company ID">
                                </div>
                            </div>
                            
                            <div class="form-row">
                                <div class="form-group">
                                    <label for="contactId">Contact ID:</label>
                                    <input 
                                        id="contactId"
                                        type="text" 
                                        [(ngModel)]="rfqParams.contactId" 
                                        class="form-control"
                                        placeholder="Enter Contact ID">
                                </div>
                                <div class="form-group">
                                    <label for="rfqPartId">RFQ Part ID:</label>
                                    <input 
                                        id="rfqPartId"
                                        type="text" 
                                        [(ngModel)]="rfqParams.rfqPartId" 
                                        class="form-control"
                                        placeholder="Enter RFQ Part ID">
                                </div>
                            </div>
                            
                            <div class="form-row">
                                <div class="form-group">
                                    <label for="isTrackingEnabled">Tracking:</label>
                                    <select 
                                        id="isTrackingEnabled"
                                        [(ngModel)]="rfqParams.isTrackingEnabled" 
                                        class="form-control">
                                        <option value="false">Disabled</option>
                                        <option value="true">Enabled</option>
                                    </select>
                                </div>
                                <div class="form-group">
                                    <button 
                                        class="btn btn-secondary btn-sm"
                                        (click)="resetRfqParams()">
                                        🔄 Reset
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Progress Bar -->
                    <div class="progress-section" *ngIf="route.isRunning">
                        <div class="progress-bar small">
                            <div class="progress-fill" [style.width.%]="route.progress"></div>
                        </div>
                        <small>{{ route.status }}</small>
                    </div>

                    <!-- Last Result -->
                    <div class="last-result" *ngIf="route.lastResult && !route.isRunning">
                        <div class="result-summary">
                            <span class="status-badge" [ngClass]="route.lastResult.status">
                                {{ route.lastResult.status?.toUpperCase() }}
                            </span>
                            <span class="test-count">
                                {{ route.lastResult.successCount }}/{{ route.lastResult.totalCount }} tests passed
                            </span>
                        </div>
                        <div class="result-details" *ngIf="route.lastResult.duration">
                            <small>Duration: {{ (route.lastResult.duration / 1000).toFixed(2) }}s</small>
                        </div>
                    </div>

                    <!-- Action Button -->
                    <button 
                        class="btn btn-test"
                        [ngClass]="{ 
                            'btn-primary': !route.isRunning && !route.lastResult,
                            'btn-warning': !route.isRunning && route.lastResult?.status === 'failed',
                            'btn-success': !route.isRunning && route.lastResult?.status === 'passed',
                            'btn-secondary': route.isRunning
                        }"
                        (click)="runRouteTest(route)"
                        [disabled]="route.isRunning || isRunningAllTests">
                        
                        <span *ngIf="route.isRunning">🔄 Testing...</span>
                        <span *ngIf="!route.isRunning && !route.lastResult">🧪 Run Test</span>
                        <span *ngIf="!route.isRunning && route.lastResult?.status === 'passed'">✅ Re-run Test</span>
                        <span *ngIf="!route.isRunning && route.lastResult?.status === 'failed'">⚠️ Re-run Test</span>
                    </button>
                </div>
            </div>

            <!-- Test Results Section -->
            <div class="results-section" *ngIf="lastTestReport">
                <h2>📊 Latest Test Results</h2>
                
                <div class="summary-cards">
                    <div class="summary-card">
                        <h3>{{ lastTestReport.totalTests }}</h3>
                        <p>Total Tests</p>
                    </div>
                    <div class="summary-card success">
                        <h3>{{ lastTestReport.passedTests }}</h3>
                        <p>Passed</p>
                    </div>
                    <div class="summary-card error">
                        <h3>{{ lastTestReport.failedTests }}</h3>
                        <p>Failed</p>
                    </div>
                    <div class="summary-card info">
                        <h3>{{ lastTestReport.successRate.toFixed(1) }}%</h3>
                        <p>Success Rate</p>
                    </div>
                </div>

                <div class="quick-results">
                    <div class="suite-result" *ngFor="let suite of lastTestReport.testSuites">
                        <div class="suite-header">
                            <span class="suite-name">{{ suite.name }}</span>
                            <span class="status-badge" [ngClass]="suite.status">{{ suite.status.toUpperCase() }}</span>
                        </div>
                        <div class="suite-stats">
                            <small>{{ suite.successCount }}/{{ suite.totalCount }} tests passed</small>
                            <small *ngIf="suite.duration">{{ (suite.duration / 1000).toFixed(2) }}s</small>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Instructions -->
            <div class="instructions">
                <h2>📖 How to Use</h2>
                <ol>
                    <li><strong>Individual Tests:</strong> Click the test button on any route card to test that specific page</li>
                    <li><strong>Full Suite:</strong> Click "Run All Tests" to test all routes automatically</li>
                    <li><strong>View Results:</strong> Use "View Report" to see detailed results in a new tab</li>
                    <li><strong>Download:</strong> Use "Download Report" to save the HTML report locally</li>
                </ol>
                
                <div class="test-info">
                    <h4>What Gets Tested:</h4>
                    <ul>
                        <li>✅ Page loads without errors</li>
                        <li>✅ Essential UI elements exist</li>
                        <li>✅ Form validation (where applicable)</li>
                        <li>✅ Input interactions</li>
                        <li>✅ Component rendering</li>
                        <li>✅ Navigation functionality</li>
                        <li>📄 PDF file upload simulation</li>
                    </ul>
                </div>

                <div class="test-files-info">
                    <h4>🗂️ Test Files Available:</h4>
                    <p>The framework uses demo PDF files from <code>/src/app/testing/files/</code>:</p>
                    <ul>
                        <li>📄 <strong>mount-demo.pdf</strong> - Mounting part specifications</li>
                        <li>📄 <strong>knife-foot-demo.pdf</strong> - Knife foot component drawing</li>
                        <li>📄 <strong>LiquidMixer-demo.pdf</strong> - Liquid mixer assembly</li>
                    </ul>
                    <p><small>These files are automatically used during file upload testing to simulate real user interactions.</small></p>
                </div>
            </div>
        </div>
    `,
    styles: [`
        :host {
            display: block;
            min-height: 100vh;
            overflow-y: auto;
            position: relative;
        }

        * {
            box-sizing: border-box;
        }

        .test-dashboard {
            max-width: 1200px;
            margin: 0 auto;
            padding: 20px;
            min-height: 100vh;
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            box-sizing: border-box;
        }

        .header {
            text-align: center;
            margin-bottom: 30px;
            padding: 20px;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            border-radius: 12px;
        }

        .header h1 {
            margin: 0 0 10px 0;
            font-size: 2.5rem;
        }

        .action-buttons {
            margin-top: 20px;
            display: flex;
            gap: 10px;
            justify-content: center;
            flex-wrap: wrap;
        }

        .btn {
            padding: 12px 24px;
            border: none;
            border-radius: 8px;
            cursor: pointer;
            font-weight: 600;
            text-decoration: none;
            display: inline-flex;
            align-items: center;
            gap: 8px;
            transition: all 0.3s ease;
        }

        .btn:disabled {
            opacity: 0.6;
            cursor: not-allowed;
        }

        .btn-primary { background: #007bff; color: white; }
        .btn-success { background: #28a745; color: white; }
        .btn-info { background: #17a2b8; color: white; }
        .btn-warning { background: #ffc107; color: black; }
        .btn-secondary { background: #6c757d; color: white; }
        .btn-lg { padding: 16px 32px; font-size: 1.1rem; }

        .btn:hover:not(:disabled) {
            transform: translateY(-2px);
            box-shadow: 0 4px 8px rgba(0,0,0,0.2);
        }

        .overall-progress {
            background: white;
            padding: 20px;
            border-radius: 12px;
            margin-bottom: 30px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.1);
            text-align: center;
        }

        .routes-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
            gap: 20px;
            margin-bottom: 40px;
            width: 100%;
            box-sizing: border-box;
        }

        .route-card {
            background: white;
            border-radius: 12px;
            padding: 20px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.1);
            transition: all 0.3s ease;
            border-left: 4px solid #e9ecef;
        }

        .route-card:hover {
            transform: translateY(-4px);
            box-shadow: 0 8px 24px rgba(0,0,0,0.15);
        }

        .route-card.running {
            border-left-color: #17a2b8;
            background: linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%);
        }

        .route-card.passed {
            border-left-color: #28a745;
        }

        .route-card.failed {
            border-left-color: #dc3545;
        }

        .route-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 10px;
        }

        .route-header h3 {
            margin: 0;
            color: #333;
            font-size: 1.3rem;
        }

        .route-path {
            background: #e9ecef;
            padding: 4px 8px;
            border-radius: 4px;
            font-family: monospace;
            font-size: 0.9rem;
        }

        .route-description {
            color: #666;
            margin: 10px 0;
            line-height: 1.5;
        }

        .route-info {
            margin: 10px 0;
        }

        .progress-section {
            margin: 15px 0;
        }

        .progress-bar {
            width: 100%;
            height: 8px;
            background: #e9ecef;
            border-radius: 4px;
            overflow: hidden;
            margin-bottom: 5px;
        }

        .progress-bar.small {
            height: 6px;
        }

        .progress-fill {
            height: 100%;
            background: linear-gradient(90deg, #28a745 0%, #20c997 100%);
            transition: width 0.3s ease;
        }

        .last-result {
            margin: 15px 0;
            padding: 10px;
            background: #f8f9fa;
            border-radius: 6px;
        }

        .result-summary {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 5px;
        }

        .status-badge {
            padding: 4px 8px;
            border-radius: 12px;
            font-size: 0.8rem;
            font-weight: 600;
            text-transform: uppercase;
        }

        .status-badge.passed { background: #d4edda; color: #155724; }
        .status-badge.failed { background: #f8d7da; color: #721c24; }
        .status-badge.running { background: #d1ecf1; color: #0c5460; }
        .status-badge.pending { background: #fff3cd; color: #856404; }

        .test-count {
            font-weight: 500;
        }

        .btn-test {
            width: 100%;
            margin-top: 15px;
        }

        .results-section {
            background: white;
            padding: 30px;
            border-radius: 12px;
            margin-bottom: 40px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        }

        .summary-cards {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
            gap: 15px;
            margin: 20px 0;
        }

        .summary-card {
            text-align: center;
            padding: 20px;
            border-radius: 8px;
            background: #f8f9fa;
        }

        .summary-card h3 {
            margin: 0;
            font-size: 2rem;
            font-weight: 700;
        }

        .summary-card p {
            margin: 5px 0 0 0;
            color: #666;
        }

        .summary-card.success { background: #d4edda; }
        .summary-card.error { background: #f8d7da; }
        .summary-card.info { background: #d1ecf1; }

        .quick-results {
            margin-top: 20px;
        }

        .suite-result {
            padding: 10px;
            margin: 5px 0;
            background: #f8f9fa;
            border-radius: 6px;
            border-left: 3px solid #dee2e6;
        }

        .suite-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 5px;
        }

        .suite-name {
            font-weight: 600;
        }

        .suite-stats {
            display: flex;
            gap: 15px;
        }

        .instructions {
            background: white;
            padding: 30px;
            border-radius: 12px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        }

        .instructions h2 {
            margin-top: 0;
            color: #333;
        }

        .instructions ol {
            line-height: 1.8;
        }

        .test-info {
            margin-top: 20px;
            padding: 20px;
            background: #f8f9fa;
            border-radius: 8px;
        }

        .test-info ul {
            line-height: 1.6;
        }

        .test-files-info {
            margin-top: 20px;
            padding: 20px;
            background: #e8f4f8;
            border-radius: 8px;
            border-left: 4px solid #17a2b8;
        }

        .test-files-info h4 {
            margin-top: 0;
            color: #0c5460;
        }

        .test-files-info code {
            background: #fff;
            padding: 2px 6px;
            border-radius: 3px;
            font-family: 'Courier New', monospace;
        }

        .test-files-info ul {
            line-height: 1.8;
        }

        .test-files-info li {
            margin: 5px 0;
        }

        @media (max-width: 1024px) {
            .test-dashboard {
                padding: 15px;
                max-width: 100%;
            }
            
            .routes-grid {
                grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
                gap: 15px;
            }
        }

        @media (max-width: 768px) {
            .test-dashboard {
                padding: 10px;
            }
            
            .routes-grid {
                grid-template-columns: 1fr;
                gap: 15px;
            }
            
            .action-buttons {
                flex-direction: column;
                align-items: center;
            }
            
            .btn {
                width: 100%;
                max-width: 300px;
            }
            
            .header h1 {
                font-size: 2rem;
            }
            
            .summary-cards {
                grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
                gap: 10px;
            }
        }

        @media (max-width: 480px) {
            .test-dashboard {
                padding: 8px;
            }
            
            .header {
                padding: 15px;
            }
            
            .header h1 {
                font-size: 1.8rem;
            }
            
            .route-card {
                padding: 15px;
            }
            
            .btn {
                padding: 10px 20px;
            }
        }

        .instructions h4 {
            color: #2c3e50;
            margin-bottom: 10px;
        }

        .quote-assist-config {
            background: #f8f9fa;
            border: 1px solid #dee2e6;
            border-radius: 8px;
            padding: 20px;
            margin-bottom: 30px;
        }

        .quote-assist-config h2 {
            color: #2c3e50;
            margin-bottom: 10px;
            font-size: 1.5rem;
        }

        .quote-assist-config p {
            color: #6c757d;
            margin-bottom: 20px;
        }

        .config-form .form-row {
            display: flex;
            gap: 20px;
            margin-bottom: 15px;
            align-items: end;
        }

        .config-form .form-group {
            flex: 1;
        }

        .config-form label {
            display: block;
            font-weight: 600;
            color: #495057;
            margin-bottom: 5px;
            font-size: 0.9rem;
        }

        .config-form .form-control {
            width: 100%;
            padding: 8px 12px;
            border: 1px solid #ced4da;
            border-radius: 4px;
            font-size: 0.9rem;
            transition: border-color 0.15s ease-in-out;
        }

        .config-form .form-control:focus {
            outline: none;
            border-color: #007bff;
            box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
        }

        @media (max-width: 768px) {
            .config-form .form-row {
                flex-direction: column;
                gap: 15px;
            }
        }

        /* Compact configuration form for route cards */
        .route-card .quote-assist-config {
            background: #f8f9fa;
            border: 1px solid #e9ecef;
            border-radius: 6px;
            padding: 15px;
            margin: 15px 0;
        }

        .route-card .quote-assist-config h4 {
            color: #495057;
            margin: 0 0 5px 0;
            font-size: 1rem;
        }

        .route-card .quote-assist-config p {
            color: #6c757d;
            margin: 0 0 10px 0;
            font-size: 0.85rem;
        }

        .config-form.compact .form-row {
            display: flex;
            gap: 10px;
            margin-bottom: 10px;
            align-items: end;
        }

        .config-form.compact .form-group {
            flex: 1;
        }

        .config-form.compact label {
            display: block;
            font-weight: 500;
            color: #495057;
            margin-bottom: 3px;
            font-size: 0.8rem;
        }

        .config-form.compact .form-control {
            width: 100%;
            padding: 6px 8px;
            border: 1px solid #ced4da;
            border-radius: 3px;
            font-size: 0.8rem;
            transition: border-color 0.15s ease-in-out;
        }

        .config-form.compact .form-control:focus {
            outline: none;
            border-color: #007bff;
            box-shadow: 0 0 0 0.1rem rgba(0, 123, 255, 0.25);
        }

        .config-form.compact .btn-sm {
            padding: 6px 12px;
            font-size: 0.8rem;
            margin-top: 15px;
        }

        @media (max-width: 768px) {
            .config-form.compact .form-row {
                flex-direction: column;
                gap: 8px;
            }
        }
    `]
})
export class UiTestDashboardComponent implements OnInit, OnDestroy {
    routes: RouteInfo[] = [
        {
            path: '',
            name: 'Home (Part Upload)',
            component: 'PartFileUploadComponent',
            description: 'Main landing page with file upload functionality for part drawings and specifications.',
            isRunning: false,
            progress: 0,
            status: ''
        },
        {
            path: 'upload',
            name: 'Part File Upload',
            component: 'PartFileUploadComponent',
            description: 'Upload and process part files including PDFs, CAD files, and technical drawings.',
            isRunning: false,
            progress: 0,
            status: ''
        },
        {
            path: 'summary',
            name: 'Quote Summary',
            component: 'QuoteSummaryComponent',
            description: 'Display comprehensive quote summary with pricing, timelines, and manufacturing details.',
            isRunning: false,
            progress: 0,
            status: ''
        },
        {
            path: 'assist',
            name: 'Quote Assist',
            component: 'QuoteAssistSingleApiComponent',
            description: 'AI-powered quote assistance with automated analysis and pricing calculations.',
            isRunning: false,
            progress: 0,
            status: ''
        },
        {
            path: 'direct-rfq-list',
            name: 'Direct RFQ List',
            component: 'DirectRfqListComponent',
            description: 'Manage and browse direct requests for quotes with filtering and search capabilities.',
            isRunning: false,
            progress: 0,
            status: ''
        },
        {
            path: 'direct-assist',
            name: 'Direct Quote Assist',
            component: 'DirectQuoteAssistComponent',
            description: 'Direct quotation workflow with manual input and AI-assisted calculations.',
            isRunning: false,
            progress: 0,
            status: ''
        },
        {
            path: 'direct-quote-summary',
            name: 'Direct Quote Summary',
            component: 'DirectQuoteSummaryComponent',
            description: 'Summary view for directly created quotes with export and sharing options.',
            isRunning: false,
            progress: 0,
            status: ''
        },
        {
            path: 'rfq-details-drawer',
            name: 'RFQ Details Drawer',
            component: 'RfqDetailsDrawerComponent',
            description: 'Detailed view panel for RFQ information and specifications.',
            isRunning: false,
            progress: 0,
            status: ''
        },
        {
            path: 'rfq-intake',
            name: 'RFQ Intake',
            component: 'RfqIntakeComponent',
            description: 'Multi-step form for collecting detailed RFQ requirements and specifications.',
            isRunning: false,
            progress: 0,
            status: ''
        }
    ];

    lastTestReport: TestReport | null = null;
    isRunningAllTests = false;
    overallProgress = 0;
    overallStatus = '';

    // RFQ Parameters for Quote Assist Testing
    rfqParams = {
        rfqId: '181b9f92-6a0a-4e57-9f9c-4439f80e9cb1',
        companyId: '1872561',
        contactId: '1442487',
        rfqPartId: '1',
        isTrackingEnabled: 'false'
    };

    private subscriptions: Subscription[] = [];

    constructor(
        private testRunner: TestRunnerService,
        private testEngine: TestEngineService,
        private router: Router
    ) {}

    ngOnInit(): void {
        // Subscribe to test results
        this.subscriptions.push(
            this.testRunner.getTestResults().subscribe(report => {
                this.lastTestReport = report;
                this.updateRouteResults(report);
            })
        );

        // Subscribe to test progress
        this.subscriptions.push(
            this.testRunner.getTestProgress().subscribe(progress => {
                const route = this.routes.find(r => r.name === progress.route);
                if (route) {
                    route.progress = progress.progress;
                    route.status = progress.status;
                    route.isRunning = progress.progress < 100 && !progress.status.startsWith('Error');
                }
            })
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    async runRouteTest(route: RouteInfo): Promise<void> {
        try {
            route.isRunning = true;
            route.progress = 0;
            route.status = 'Initializing...';

            // Pass RFQ parameters only for Direct Quote Assist
            if (route.name === 'Direct Quote Assist') {
                await this.testRunner.runTestsForRoute(route.path, route.name, this.rfqParams);
            } else {
                await this.testRunner.runTestsForRoute(route.path, route.name);
            }
        } catch (error) {
            console.error(`Test failed for route ${route.name}:`, error);
            route.isRunning = false;
            route.status = `Error: ${error instanceof Error ? error.message : String(error)}`;
        }
    }

    async runAllTests(): Promise<void> {
        try {
            this.isRunningAllTests = true;
            this.overallProgress = 0;
            this.overallStatus = 'Starting full test suite...';

            // Reset all route statuses
            this.routes.forEach(route => {
                route.isRunning = false;
                route.progress = 0;
                route.status = '';
                route.lastResult = undefined;
            });

            let completedRoutes = 0;
            const totalRoutes = this.routes.length;

            for (const route of this.routes) {
                this.overallStatus = `Testing ${route.name}...`;
                await this.runRouteTest(route);
                
                completedRoutes++;
                this.overallProgress = (completedRoutes / totalRoutes) * 100;
            }

            this.overallStatus = 'All tests completed!';
            setTimeout(() => {
                this.isRunningAllTests = false;
                this.overallProgress = 0;
                this.overallStatus = '';
            }, 3000);

        } catch (error) {
            console.error('Failed to run all tests:', error);
            this.isRunningAllTests = false;
            this.overallStatus = `Error: ${error instanceof Error ? error.message : String(error)}`;
        }
    }

    downloadLastReport(): void {
        if (this.lastTestReport) {
            this.testRunner.generateAndDownloadReport(this.lastTestReport);
        }
    }

    viewLastReport(): void {
        if (this.lastTestReport) {
            this.testRunner.openReportInNewTab(this.lastTestReport);
        }
    }

    private updateRouteResults(report: TestReport): void {
        report.testSuites.forEach(suite => {
            const route = this.routes.find(r => r.name === suite.name);
            if (route) {
                route.lastResult = suite;
                route.isRunning = false;
            }
        });
    }

    resetRfqParams(): void {
        this.rfqParams = {
            rfqId: '181b9f92-6a0a-4e57-9f9c-4439f80e9cb1',
            companyId: '1872561',
            contactId: '1442487',
            rfqPartId: '1',
            isTrackingEnabled: 'false'
        };
    }
} 